import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updatePassword,
  deleteUser as firebaseDeleteUser,
  EmailAuthProvider,
  reauthenticateWithCredential,
  getAuth,
  updateEmail,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../services/firebase';
import { 
  createUserProfile, 
  getUserProfile,
  updateLastActivity,
  updateLastLogin,
  updateLogout,
  updatePasswordChanged,
  deleteUserAccount
} from '../services/userService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signup(email, password) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Cria o perfil do usuário com dados zerados
      await createUserProfile(userCredential.user.uid, email);
      
      return userCredential;
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      throw error;
    }
  }

  async function login(email, password) {
    try {
      console.log('LOGIN CONTEXT: Chamando login com', email);
      console.log('1. Iniciando processo de login');
      
      // Primeiro tenta autenticar com o Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      console.log('2. Usuário autenticado com sucesso:', user.email);
      
      // Atualiza o campo lastLogin no Firestore
      console.log('Chamando updateLastLogin para', user.uid);
      updateLastLogin(user.uid).catch(error => {
        console.warn('Erro ao atualizar lastLogin (ignorando):', error);
      });
      
      // Tenta carregar o perfil do usuário
      try {
        console.log('3. Carregando perfil do usuário');
        const profile = await getUserProfile(user.uid);
        setUserProfile(profile);
        console.log('4. Perfil carregado com sucesso');
      } catch (profileError) {
        console.warn('Erro ao carregar perfil (continuando com login):', profileError);
        // Se não conseguir carregar o perfil, usa um perfil vazio
        setUserProfile(null);
      }
      
      // Tenta atualizar a última atividade de forma não-bloqueante
      updateLastActivity(user.uid).catch(error => {
        console.warn('Erro ao atualizar última atividade (ignorando):', error);
      });
      
      console.log('5. Login local concluído');
      return user;
    } catch (error) {
      console.error('Erro ao fazer login:', error);
      throw error;
    }
  }

  async function logout() {
    try {
      console.log('5. Iniciando logout no AuthContext');

      // Faz o signOut no Firebase primeiro
      console.log('6. Fazendo signOut no Firebase');
      await signOut(auth);

      // Depois atualiza o status no Firestore
      if (currentUser) {
        console.log('7. Atualizando status no Firestore');
        await updateLogout(currentUser.uid);
      }

      console.log('8. Limpando estados do contexto');
      setCurrentUser(null);
      setUserProfile(null);

      console.log('9. Logout concluído com sucesso');
      return true;
    } catch (error) {
      console.error('Erro no processo de logout:', error);
      throw error;
    }
  }

  async function changePassword(uid, newPassword) {
    try {
      if (!uid) throw new Error('ID do usuário não fornecido');
      
      // Atualiza a senha no Firebase Auth
      const user = auth.currentUser;
      if (user && user.uid === uid) {
        // Se for o usuário atual, atualiza diretamente
        await updatePassword(user, newPassword);
      } else {
        // Se for outro usuário, precisamos forçar ele a redefinir a senha no próximo login
        // Por enquanto, vamos apenas atualizar no Firestore
        console.warn('Alteração de senha de outro usuário: será necessário redefinir a senha no próximo login');
      }
      
      // Atualiza a senha no Firestore
      await updatePasswordChanged(uid, newPassword);
      
      return true;
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      throw error;
    }
  }

  async function deleteUser(uid) {
    try {
      await deleteUserAccount(uid);
      return true;
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      throw error;
    }
  }

  async function resetPassword(email) {
    try {
      auth.languageCode = 'pt'; // Define o idioma para português
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      console.error('Erro ao enviar email de redefinição de senha:', error);
      throw error;
    }
  }

  useEffect(() => {
    console.log('Iniciando monitoramento de autenticação...');
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Estado de autenticação alterado:', user ? 'Usuário autenticado' : 'Usuário não autenticado');
      setCurrentUser(user);
      
      if (user) {
        try {
          console.log('Carregando perfil do usuário:', user.uid);
          const profile = await getUserProfile(user.uid);
          console.log('Perfil carregado:', profile);
          setUserProfile(profile);
        } catch (error) {
          console.warn('Erro ao carregar perfil do usuário (continuando):', error);
          // Se não conseguir carregar o perfil, mantém o estado atual
        }
      } else {
        console.log('Usuário não autenticado, limpando perfil');
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    logout,
    changePassword,
    deleteUser,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 